import { useState, useCallback } from 'react';
import { useTTS } from '../../lib/tts/useTTS';
import { extractArticleText } from '../../lib/text/extractArticleText';

const SPEED_OPTIONS = [1.0, 1.25, 1.5] as const;

export default function TTSControls() {
  const { status, rate, errorMessage, play, pause, resume, stop, setRate } = useTTS();
  const [showSpeedMenu, setShowSpeedMenu] = useState(false);

  const handlePlay = useCallback(() => {
    console.log('[TTS] handlePlay called, current status:', status);
    
    if (status === 'paused') {
      console.log('[TTS] Resuming from paused state');
      resume();
      return;
    }
    
    console.log('[TTS] Extracting article text...');
    const text = extractArticleText();
    
    if (!text) {
      console.error('[TTS] No text extracted, cannot play');
      return;
    }
    
    console.log(`[TTS] Text extracted successfully, length: ${text.length}`);
    console.log('[TTS] Calling play()...');
    play(text);
  }, [status, play, resume]);

  const handleStop = useCallback(() => {
    console.log('[TTS] handleStop called');
    stop();
  }, [stop]);

  const handlePause = useCallback(() => {
    console.log('[TTS] handlePause called');
    pause();
  }, [pause]);

  const handleSpeedChange = useCallback((speed: number) => {
    console.log(`[TTS] Speed changed to ${speed}x`);
    setRate(speed);
    setShowSpeedMenu(false);
  }, [setRate]);

  const isActive = status === 'playing' || status === 'paused';

  return (
    <div className="flex items-center gap-1.5 relative">
      {/* 再生 / 一時停止 */}
      {status === 'playing' ? (
        <button
          onClick={handlePause}
          className="w-8 h-8 flex items-center justify-center rounded-md border border-cosmic-accent-cyan/40 bg-cosmic-accent-cyan/10 text-cosmic-accent-cyan hover:bg-cosmic-accent-cyan/20 transition-all duration-200 cursor-pointer"
          title="一時停止"
        >
          <i className="ri-pause-line text-base"></i>
        </button>
      ) : (
        <button
          onClick={handlePlay}
          className={`w-8 h-8 flex items-center justify-center rounded-md border transition-all duration-200 cursor-pointer ${
            status === 'paused'
              ? 'border-cosmic-accent-cyan/40 bg-cosmic-accent-cyan/10 text-cosmic-accent-cyan hover:bg-cosmic-accent-cyan/20'
              : 'border-cosmic-border text-cosmic-text-secondary hover:border-cosmic-accent-cyan/40 hover:text-cosmic-accent-cyan'
          }`}
          title={status === 'paused' ? '再開' : '読み上げ'}
        >
          <i className="ri-play-line text-base"></i>
        </button>
      )}

      {/* 停止（再生中/一時停止中のみ） */}
      {isActive && (
        <button
          onClick={handleStop}
          className="w-8 h-8 flex items-center justify-center rounded-md border border-cosmic-border text-cosmic-text-secondary hover:border-red-400/40 hover:text-red-400 transition-all duration-200 cursor-pointer"
          title="停止"
        >
          <i className="ri-stop-line text-base"></i>
        </button>
      )}

      {/* 速度 */}
      <div className="relative">
        <button
          onClick={() => setShowSpeedMenu((v) => !v)}
          className="h-8 px-2 flex items-center justify-center rounded-md border border-cosmic-border text-cosmic-text-secondary hover:border-cosmic-accent-cyan/40 hover:text-cosmic-accent-cyan transition-all duration-200 cursor-pointer text-xs font-medium whitespace-nowrap"
          title="速度変更"
        >
          {rate}x
        </button>

        {showSpeedMenu && (
          <>
            <div
              className="fixed inset-0 z-10"
              onClick={() => setShowSpeedMenu(false)}
            ></div>
            <div className="absolute top-full left-0 mt-1 z-20 bg-cosmic-card border border-cosmic-border rounded-md shadow-lg shadow-black/40 overflow-hidden animate-fade-in">
              {SPEED_OPTIONS.map((s) => (
                <button
                  key={s}
                  onClick={() => handleSpeedChange(s)}
                  className={`block w-full px-4 py-2 text-xs text-left whitespace-nowrap cursor-pointer transition-colors ${
                    rate === s
                      ? 'text-cosmic-accent-cyan bg-cosmic-accent-cyan/10'
                      : 'text-cosmic-text-secondary hover:text-cosmic-text-primary hover:bg-cosmic-card'
                  }`}
                >
                  {s}x
                </button>
              ))}
            </div>
          </>
        )}
      </div>

      {/* ステータスインジケーター */}
      {status === 'playing' && (
        <span className="ml-1 flex items-center gap-1 text-xs text-cosmic-accent-cyan">
          <span className="w-1.5 h-1.5 rounded-full bg-cosmic-accent-cyan animate-pulse"></span>
          再生中
        </span>
      )}
      {status === 'paused' && (
        <span className="ml-1 text-xs text-cosmic-text-secondary">一時停止</span>
      )}

      {/* エラー */}
      {status === 'error' && errorMessage && (
        <span className="ml-1 text-xs text-red-400">{errorMessage}</span>
      )}
    </div>
  );
}
